import Events from "./Utils/Events";
import { config } from "../config";

/* 
  This class is responsible for updating the content of the application based on the current state.
  It handles the artwork and persona sections, including preloading videos and updating text content.
*/

class ContentUpdater {
  // Constructor initializes state, loader, and target elements for updating content
  constructor(state, loader, targets) {
    this.state = state; // Holds the current application state
    this.loader = loader; // Loader instance to handle video preloading
    this.artworkTargets = targets.artworkTargets; // Elements that display artwork content
    this.personaTargets = targets.personaTargets; // Elements that display persona content
    this.personaVideos = []; // Array to store preloaded persona videos

    this.hideArtworkTargets(this.state); // Hides artwork targets with missing data
  }

  hideArtworkTargets(state) {
    // loop thought state and check for missing values
    this.artworkTargets.forEach((target) => {
      const artworkType = target.dataset.artwork; // Gets the artwork type from the target element
      const artworkData = state[artworkType]; // Retrieves the corresponding artwork data from the state

      
      // If the artwork data is missing, hide the target element
      if (!artworkData || artworkData.value === 0) {
        target.style.display = "none"; // Hides the target element if no artwork data is available
      } 
    });
  }

  // Main method to update content and initialize video carousel
  updateContent(videoCarousel) {
    this.updateArtworkContent(); // Updates the artwork section
    this.updatePersonaContent(); // Updates the persona section

    this.initializeVideoCarousel(videoCarousel); // Initializes the video carousel with persona videos

    // Emit an event after content is updated, slightly delayed to ensure all updates are applied
    setTimeout(() => {
      Events.emit("content.updated");
      this.disableContextMenu(); // Disables context menu for artwork images
    }, 200);
  }

  // Updates the artwork content for each target element
  updateArtworkContent() {
    this.artworkTargets.forEach((target) => {
      const { artwork, value } = this.state[target.dataset.artwork] || {}; // Retrieves artwork data from state
      const artworkImageTarget = target.querySelector(".artwork__image"); // Targets artwork image element

      // add non draggable attribute to image to prevent user from saving
      artworkImageTarget.setAttribute("draggable", "false");

      const artworkValueTarget = target.querySelector(
        ".artwork__text__stat--value"
      ); // Targets value element
      const artworkTitleTarget = target.querySelector(".artwork__text__title"); // Targets title element
      const artworkArtistTarget = target.querySelector(
        ".artwork__text__artist"
      ); // Targets artist element
      const artworkCopyrightTarget = target.querySelectorAll(
        ".artwork__text__copyright"
      ); // Targets copyright element

      if (artwork) {
        // Updates artwork information if available
        this.setArtworkInfo(
          artworkImageTarget,
          artworkTitleTarget,
          artworkArtistTarget,
          artworkCopyrightTarget,
          artwork
        );
      }

      if (value) {
        artworkValueTarget.innerText = value; // Sets the value if available
      }

      // Update specific content based on the artwork type (event or era)
      if (target.dataset.artwork === "event") {
        this.updateEventContent(target); // Updates event-specific content
      }

      if (target.dataset.artwork === "era") {
        this.updateEraContent(target); // Updates era-specific content
      }
    });
  }

  // Updates content for the "event" artwork type
  updateEventContent(target) {
    const event = this.state.event; // Retrieves event data from state
    const eventLinkTarget = target.querySelector("a"); // Targets the event link element
    const descriptionTarget = target.querySelector(
      ".artwork__text__description"
    ); // Targets description element
    const artworkImageTarget = target.querySelector(".artwork__image"); // Targets artwork image element
    const artworkValueTarget = target.querySelector(
      ".artwork__text__stat--value"
    ); // Targets value element
    const artworkCopyrightTarget = target.querySelectorAll(
      ".artwork__text__copyright"
    ); // Targets copyright element

    // Set event data to the corresponding DOM elements

    if (event) {
      eventLinkTarget.href = event.event_url
        ? `${event.event_url}?VID=${this.state.visitor.id}`
        : "#"; // Sets the event link URL
      eventLinkTarget.target = "_blank"; // Opens the link in a new tab
      artworkImageTarget.src = `/assets/images/artwork/${event.image_filename}`;
      artworkValueTarget.innerText = event.name;
      descriptionTarget.innerText = `${event.description} ${event.date}.`;

      // Update copyright info for both mobile and desktop layouts
      artworkCopyrightTarget.forEach(
        (element) =>
          (element.innerHTML = `${event.image_artist}. <span>${event.image_title}</span>, ${event.image_copyright}`)
      );

      // if the event id is 2, add a class to the value target
      if (event.name.length > 18) {
        artworkValueTarget.classList.add("long-text");
      } else {
        artworkValueTarget.classList.remove("long-text");
      }
    } else {
      // Hides the event section if no event data is available
      document.querySelector("[data-artwork='event']").style.display = "none";
    }
  }

  disableContextMenu() {
    this.artworkTargets.forEach((target) => {
      target.addEventListener("contextmenu", (event) => {
        if (event.target.tagName === "IMG") {
          event.preventDefault();
        }
      });
    });
  }

  // Updates content for the "era" artwork type
  updateEraContent(target) {
    const { image_filename, name } = this.state.era; // Retrieves era data from state
    const artworkImageTarget = target.querySelector(".artwork__image"); // Targets artwork image element
    // const artworkImageTargetMobile = target.querySelector(".artwork__image--mobile"); // Targets artwork image element for mobile
    const artworkValueTarget = target.querySelector(
      ".artwork__text__stat--value"
    ); // Targets value element

    const colorBlobs = target.querySelectorAll(".artwork__image .blob"); // Targets color blob elements

    // Set the color of the blobs based on the era colors
    colorBlobs.forEach((blob, index) => {
      const eraColors = config.eraColors[name.toLowerCase()];
      if (eraColors && eraColors[index]) {
        blob.style.setProperty("--color", eraColors[index]); // Sets the color of each blob
      }
    });
    // SVG gradient elements
    // const ellipses = Array.from(artworkImageTarget.querySelectorAll("ellipse"));

    // Get the era colors from the config file
    const eraColors = config.eraColors[name.toLowerCase()];

    // Set the era colors to the SVG gradient elements
    // ellipses.forEach((ellipse, index) => {
    //   ellipse.style.fill = eraColors[index];
    // });

    // Set era data to the corresponding DOM elements
    artworkImageTarget.src = `/assets/images/${image_filename}`;
    artworkValueTarget.innerText = name;
  }

  // Updates persona content for each target element
  updatePersonaContent() {
    this.personaTargets.forEach((target) => {
      const persona = this.state.personas.find(
        (persona) => persona.name === target.dataset.persona
      ); // Finds the corresponding persona in the state

      if (persona) {
        this.setPersonaInfo(target, persona); // Sets persona info in the target element
      }

      this.toggleSubtitleVisibility(target); // Toggles the visibility of the subtitle
    });
  }

  // Helper method to set the artwork information in the DOM elements
  setArtworkInfo(
    imageTarget,
    titleTarget,
    artistTarget,
    copyrightTarget,
    artwork
  ) {
    const { image_filename, image_title, image_artist, image_copyright } =
      artwork;

    imageTarget.src = `/assets/images/artwork/${image_filename}`;
    titleTarget.innerText = image_title;
    artistTarget.innerText = image_artist;

    // if the image title is less than 20 characters, add a class to the title target to set line breaks
    if (image_title.length < 18) {
      titleTarget.classList.add("short-text");
    } else {
      titleTarget.classList.remove("short-text");
    }

    copyrightTarget.forEach(
      (element) =>
        (element.innerHTML = `${image_artist}. <span>${image_title}</span>, ${image_copyright}`)
    );
  }

  // Helper method to set the persona information in the DOM elements
  setPersonaInfo(target, persona) {
    const {
      video_filename: videoUrl,
      title,
      description,
      description_txt: descriptionText,
    } = persona;

    // Map the target elements with persona data
    const targetMap = {
      ".persona__video": videoUrl,
      ".persona__overlay__title--two": title?.split(" ")[1],
      ".persona__overlay__description__title": description?.join(" / "),
      ".persona__overlay__description__text": descriptionText,
    };

    // Update the elements with the corresponding persona data
    Object.entries(targetMap).forEach(([selector, value]) => {
      const elements = target.querySelectorAll(selector);
      elements.forEach((element) => {
        if (element && value) {
          if (selector === ".persona__video") {
            this.handleVideoElement(element, value); // Handles the video element separately
          } else {
            element.innerText = value; // Sets text content for other elements
          }
        }
      });
    });
  }

  // Handles video element updates, including preloading logic
  handleVideoElement(element, value) {
    const preloadedVideo = this.loader.getPreloadedVideo(value); // Gets preloaded video data if available
    const ariaLabel = element.getAttribute("aria-label");

    if (preloadedVideo && preloadedVideo.videoBlobUrl) {
      const videoElement = preloadedVideo.video;

      if (preloadedVideo.video.preloaded) {
        console.log("Video is fully preloaded:", value); // Logs when video is fully preloaded
        videoElement.classList.add("persona__video"); // Adds the correct class to the video
        videoElement.setAttribute("aria-label", ariaLabel); // Updates aria-label for accessibility
        this.personaVideos.push(videoElement); // Stores the preloaded video element
        element.replaceWith(videoElement); // Replaces the original element with the preloaded video element
      } else {
        console.log("Video is not fully preloaded:", value); // Handle case where video isn't fully preloaded
      }
    } else {
      console.log("Preloaded video not found:", value); // Fallback if no preloaded video is found
    }
  }

  // Toggles the visibility of the persona subtitle based on whether it matches the current persona
  toggleSubtitleVisibility(target) {
    const subtitle = target.querySelector(".persona__overlay__subtitle");
    const shareButtons = target.querySelectorAll(".persona__overlay__share");

    const isUserPersona = this.state.persona.name === target.dataset.persona;

    subtitle.style.visibility = isUserPersona ? "visible" : "hidden";
    shareButtons.forEach((button) => {
      button.style.display = isUserPersona ? "block" : "none";
    });
    subtitle.setAttribute("aria-hidden", isUserPersona ? "false" : "true"); // Updates accessibility attributes
  }

  // Initializes the video carousel with the correct persona video
  initializeVideoCarousel(videoCarousel) {
    // Finds the index of the user persona in the persona targets
    const userPersonaIndex = this.personaTargets.findIndex(
      (target) => target.dataset.persona === this.state.persona.name
    );

    videoCarousel.initializeSwiper(userPersonaIndex); // Initializes Swiper carousel based on persona ID
  }
}

export default ContentUpdater;
